// The Team dock's client state (staff chat MVP, SR-14): collapsed or open, the open conversation, and the New message
// pane ("To:" search). Anything on the page opens the dock through here: the Team home card, the /messages/:threadId
// deep link, "Message a colleague" (patient switch).
import { create } from 'zustand';

export interface DockCompose {
  /** A patient thread is being started ("Message a colleague", behind ?x=staff-chat-patient). */
  patientId?: string;
}

/** The first message of a new conversation, written in New message before the thread existed; the thread sends it. */
export interface FirstMessage {
  threadId: string;
  body: string;
  clientMessageId: string;
}

interface DockState {
  open: boolean;
  /** The conversation on the right, or none ("Choose a conversation, or start a new message."). */
  threadId: string | null;
  /** The New message pane is on the right instead of a conversation. */
  compose: DockCompose | null;
  firstMessage: FirstMessage | null;
  /** Open the conversation just created in New message, with its first message to send. */
  openWithFirstMessage: (first: FirstMessage) => void;
  /** The thread took its first message. */
  takeFirstMessage: () => void;
  /** Open the dock; on a conversation when one is given, else on what was open before. */
  openDock: (threadId?: string | null) => void;
  /** Open the dock on the New message pane. */
  startNewMessage: (compose?: DockCompose) => void;
  /** Show a conversation on the right (the dock stays open). */
  selectThread: (threadId: string) => void;
  /** Leave the New message pane without picking anyone. */
  cancelCompose: () => void;
  collapse: () => void;
  /** Back to the first state: collapsed, nothing chosen (a user change, tests, stories). */
  reset: () => void;
}

export const useDockStore = create<DockState>((set) => ({
  open: false,
  threadId: null,
  compose: null,
  firstMessage: null,
  openWithFirstMessage: (first) => set({ open: true, threadId: first.threadId, compose: null, firstMessage: first }),
  takeFirstMessage: () => set({ firstMessage: null }),
  openDock: (threadId) => set((s) => ({ open: true, threadId: threadId ?? s.threadId, compose: threadId ? null : s.compose })),
  startNewMessage: (compose = {}) => set({ open: true, compose }),
  selectThread: (threadId) => set({ open: true, threadId, compose: null }),
  cancelCompose: () => set({ compose: null }),
  collapse: () => set({ open: false }),
  reset: () => set({ open: false, threadId: null, compose: null, firstMessage: null }),
}));
